import type { SerializedState } from "@synapse/engine";
import type { GameState } from "../stores/useGameStoreFacade";
import type { DailyChallengeProgress } from "../types/dailyChallenges";
import { type GameHistoryEntry, migrateHistory } from "../utils/gameHistory";

import type { SeatSave } from "../ariadne/seat";

import type { MoveRecord } from "../utils/gameReportUtils";

// ============================================================================
// UNIFIED DATA SCHEMA - the shape of all app data, its defaults and migrations
// ============================================================================

// 3: gameHistory holds slim GameHistoryEntry objects instead of full GameReports.
export const CURRENT_SCHEMA_VERSION = 3;

/** One-time, idempotent upgrade of loaded data to the current schema. */
export function migrateData(data: UnifiedAppData): UnifiedAppData {
  const version = data.meta?.schemaVersion ?? 0;
  // Fill any missing top-level section with its default.
  const defaults = createDefaultData() as unknown as Record<string, unknown>;
  const record = data as unknown as Record<string, unknown>;
  for (const key of Object.keys(defaults)) {
    if (record[key] === undefined || record[key] === null) {
      record[key] = defaults[key];
    }
  }
  if (version < 3) {
    data.gameHistory = migrateHistory(data.gameHistory);
    data.meta = { ...data.meta, schemaVersion: 3 };
  }
  return data;
}

/** Her record against the player, dailies only (spec §5, D10). */
export interface AriadneRecord {
  ahead: number;
  behind: number;
  tied: number;
  left_behind: number;
  /** Games given up: on the record so it adds up to the games she learned from. Absent in records saved before 2026-10-20. */
  gave_up: number;
  /** Current and best run of not-behind games. */
  streak: number;
  best: number;
}

/** Spec §6: everything she is, per player. Under 2 KB. */
export interface AriadneSection {
  version: 1;
  /** The engine's SerializedState (counts, games, base64 atlas), or null before her first game. */
  state: SerializedState | null;
  record: AriadneRecord;
  /** The thread toggle's persisted default (spec D4): off until the player turns her on. */
  show_thread: boolean;
}

export const createDefaultAriadne = (): AriadneSection => ({
  version: 1,
  state: null,
  record: {
    ahead: 0,
    behind: 0,
    tied: 0,
    left_behind: 0,
    gave_up: 0,
    streak: 0,
    best: 0,
  },
  show_thread: false,
});

// Main data structure that contains everything
export interface UnifiedAppData {
  // User Profile & Settings
  user: {
    tutorialComplete: boolean;
    hasPlayedBefore: boolean;
    createdAt: number;
    lastActiveAt: number;

    // No monetization - pure gaming experience

    // Privacy settings (for sharing features)
    privacy?: {
      allowChallengeSharing: boolean; // Can share challenge links
      allowStatsSharing: boolean; // Can share stats/achievements
      dataCollection: boolean; // Allow anonymized analytics
    };
  };

  // Game Statistics & Progress
  stats: {
    totalGamesPlayed: number;
    totalWins: number;
    totalGaveUps: number;
    achievementsUnlocked: number;
    cumulativeMoveAccuracySum: number;
    // Add more stats as needed
  };

  // Every game ever played, newest first (slim entries; uncapped). Lifetime
  // stats and whole-history pair dedup both read it. Entries are immutable:
  // persistence stores each one once, as its own record.
  gameHistory: GameHistoryEntry[];

  // Achievements
  achievements: {
    unlockedIds: string[];
    viewedIds: string[]; // Track which achievements have been viewed/acknowledged
    // Track when achievements were unlocked for analytics and versioning
    unlockTimestamps: Record<string, number>;
    // Progressive achievement tracking - track cumulative counts for progressive achievements
    progressiveCounters: Record<string, number>;
    // Schema version for safe migrations
    schemaVersion: number;
  };

  // Word Collections Progress
  collections: {
    [collectionId: string]: {
      collectedWords: string[];
      lastUpdated: number;
    };
  };

  // Word Collection Viewing (track completed and viewed collections)
  wordCollections: {
    completedIds: string[]; // Track which collections have been completed
    viewedIds: string[]; // Track which completed collections have been viewed/acknowledged
    completionTimestamps: Record<string, number>; // Track when collections were completed
    schemaVersion: number;
  };

  // Daily Challenges
  dailyChallenges: {
    progress: Record<string, DailyChallengeProgress>;
    freeGamesRemaining: number;
    lastResetDate: string;
  };

  // Current Game States (for persistence)
  currentGames: {
    regular: PersistentGameState | null;
    challenge: PersistentGameState | null;
    temp: PersistentGameState | null;
  };

  // Ariadne (spec §6): her learned state, her record and the thread default
  ariadne: AriadneSection;

  // Metadata for versioning and device tracking
  meta: {
    version: string;
    schemaVersion: number; // For data structure migrations
    deviceId?: string; // For analytics and device identification
    firstInstallDate?: number; // Track when app was first installed
  };
}

// Game state interface (compatible with existing game store)
export interface PersistentGameState {
  startWord: string | null;
  targetWord: string | null;
  currentWord: string | null;
  playerPath: string[];
  /**
   * The gold path, fixed at game start; a resumed save keeps its stored gold
   * path. Persisted key, Ruling R6.
   */
  optimalPath: string[];
  /** The Local Optimal route; recomputed on restore. Persisted key, Ruling R6. */
  suggestedPathFromCurrent: string[];
  gameStatus: GameState["gameStatus"];
  /** Move judgements on the surviving path. Persisted key, Ruling R6. */
  optimalChoices: GameState["optimalChoices"];
  backtrackHistory: GameState["backtrackHistory"];
  /** Every move made, backtracked ones included. Absent on older saves. */
  moveHistory?: MoveRecord[];
  pathDisplayMode: GameState["pathDisplayMode"];
  startTime: number;
  isChallenge?: boolean;
  isDailyChallenge?: boolean;
  isTutorial?: boolean;
  aiPath?: string[];
  aiModel?: string | null;
  /** Her seat mid-game (spec §3): the match and her RNG position. Absent on saves from before she played. */
  match?: SeatSave;
  currentDailyChallengeId?: string;
  potentialRarestMovesThisGame?: GameState["potentialRarestMovesThisGame"];
  graphVersion?: string;
  vocabHash?: string;
}

/**
 * Get today's date in YYYY-MM-DD format using EST timezone
 * This ensures daily resets happen at 12:00 AM EST, consistent with daily challenges
 */
export const getTodayStringEST = (): string => {
  const now = new Date();

  // Convert to EST (UTC-5) or EDT (UTC-4) depending on DST
  // Using toLocaleDateString with EST timezone
  const estDate = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );

  // Format as YYYY-MM-DD
  const year = estDate.getFullYear();
  const month = String(estDate.getMonth() + 1).padStart(2, "0");
  const day = String(estDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const createDefaultData = (): UnifiedAppData => ({
  user: {
    // Local-only mode - no email needed
    tutorialComplete: false,
    hasPlayedBefore: false,
    createdAt: Date.now(),
    lastActiveAt: Date.now(),

    // Clean, monetization-free user experience

    // Default privacy settings (privacy-first)
    privacy: {
      allowChallengeSharing: true, // Enable deep link sharing by default
      allowStatsSharing: true, // Enable stats sharing by default
      dataCollection: false, // Opt-out for analytics (privacy-first)
    },
  },
  stats: {
    totalGamesPlayed: 0,
    totalWins: 0,
    totalGaveUps: 0,
    achievementsUnlocked: 0,
    cumulativeMoveAccuracySum: 0,
  },
  gameHistory: [],
  achievements: {
    unlockedIds: [],
    viewedIds: [],
    unlockTimestamps: {},
    progressiveCounters: {},
    schemaVersion: 1,
  },
  collections: {},
  dailyChallenges: {
    progress: {},
    freeGamesRemaining: 1,
    lastResetDate: getTodayStringEST(),
  },
  currentGames: {
    regular: null,
    challenge: null,
    temp: null,
  },
  ariadne: createDefaultAriadne(),
  meta: {
    version: "1.3.0-rc",
    schemaVersion: CURRENT_SCHEMA_VERSION,
    deviceId: `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    firstInstallDate: Date.now(),
  },
  wordCollections: {
    completedIds: [],
    viewedIds: [],
    completionTimestamps: {},
    schemaVersion: 1,
  },
});
